import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { useStore } from '../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { observer } from 'mobx-react';

// services
import ErrorService from '../../../../../../services/general/error';

// props

// components
import { CommandBar, ContextualMenu, ContextualMenuItemType, DefaultButton, DirectionalHint, IColumn, ICommandBarItemProps, Icon, IContextualMenuItem, IContextualMenuProps, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../typography/text';
import { IPaginationResourceShort } from '../../../../../../props/general';
import Pagination from '../../../../../uiframeworks/pagination';
import GlobalConfig from '../../../../../../config';
import { IIncomeInvoiceResourceShortProps } from '../../../../../../props/finance/incomes/invoices/invoice';
import IncomeInvoiceService from '../../../../../../services/finance/incomes/invoices/invoice';
import { exportIncomeInvoicesToExcel } from '../../../../../../services/finance/incomes/invoices/export';
import OrderService from '../../../../../../services/orders/order';
import Tag, { TTagVariant } from '../../../../../uiframeworks/tag';
import GeneralService from '../../../../../../services/general';
import IncomeInvoiceForm from '../form';
import PermissionsService from '../../../../../../services/permissions';
import IncomeInvoiceDetails from '../details';
import SelectOrder from '../../../../../uiframeworks/forms/selectOrder';
import moment from 'moment';
import Label from '../../../../../typography/label';
import ListItems from '../../../../../uiframeworks/listitems';
import { ListItemsColumnProps, ListItemsFilterByProps, ListItemsOrderByProps, ListItemsQuickFilterProps } from '../../../../../uiframeworks/listitems/props';
import SubsidiariesService from '../../../../../../services/data/subsidiaries';
import CompanyService from '../../../../../../services/users/company';
import IncomeInvoicesListFilterButton, { TFilterBy } from './filter';
import { FilterOptionProps } from '../../../../../uiframeworks/filters/panel';
import SelectedFilter from '../../../../../uiframeworks/filters/selected';

interface IncomeInvoicesListProps {
    qs?: string[];
    orderId?: string;
    invoices?: IIncomeInvoiceResourceShortProps[];
    columns?: TIncomeInvoiceListCoumn[];
    quickFilters?: ListItemsQuickFilterProps[];
    hideSearch?: boolean;
    hideFilter?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
    filters?: TFilterBy[];
}

export type TIncomeInvoiceListCoumn = 'invoiceNumber' | 'orderNumber' | 'subsidiary' | 'customer' | 'invoiceDate' | 'relatedItems' | 'orderDetails' | 'status' | 'paid' | 'unpaid' | 'amount' | 'dueDate' | 'notes';

const IncomeInvoicesList: React.FC<IncomeInvoicesListProps> = observer((props: IncomeInvoicesListProps) => {
    const shownColumns: TIncomeInvoiceListCoumn[] = props.columns || [
        'invoiceNumber',
        'orderDetails',
        'relatedItems',
        'amount',
        'paid',
        'unpaid',
        'status',
        'notes',
        'dueDate'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [invoices, setInvoices] = React.useState<IIncomeInvoiceResourceShortProps[]>(props.invoices || []);
    const [activeInvoice, setActiveInvoice] = React.useState<IIncomeInvoiceResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [filterBy, setFilterBy] = React.useState<ListItemsFilterByProps[] | undefined>();
    const [exporting, setExporting] = React.useState<boolean>(false);
    const [orderBy, setOrderBy] = React.useState<ListItemsOrderByProps | undefined>(undefined);

    const handleExportExcel = async () => {
        try {
            setExporting(true);
            await exportIncomeInvoicesToExcel(invoices, 'LAPORAN INCOME INVOICES - JEJAK LOGISTIK');
            setExporting(false);
        } catch (e) {
            setExporting(false);
            banner.add({
                key: 'export_income_invoices_error',
                text: 'Gagal mengekspor data income invoices ke Excel. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    };

    const columns: ListItemsColumnProps[] = [
        {
            key: "invoiceNumber",
            name: "Invoice Number",
            minWidth: 125,
            maxWidth: 125,
            fieldName: 'id',
            enableOrderBy: true,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Link onClick={() => {
                        setActiveInvoice(item);
                        setActiveSurface('details');
                    }}><Text size={'small'}>#{IncomeInvoiceService.getInvoiceNumber(item.id || "")}</Text></Link>
                </Stack>;
            }
        },
        {
            key: "orderNumber",
            name: "Order Number",
            minWidth: 135,
            maxWidth: 135,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.order ? <Link href={`/orders/${item.order.id}`} target={'_blank'}>
                        <Text size={'small'}>#{OrderService.getOrderNumber(item.order.orderNumber)}</Text>
                    </Link> : null}
                    {!item.order ? <Tag size={'small'} variant={'error'} text={'!!! Not related to order !!!'} /> : null}
                </Stack>;
            }
        },
        {
            key: "customer",
            name: "Customer",
            minWidth: 150,
            maxWidth: 150,
            enableFilter: true,
            onRetrieveFilterOptions: async () => {
                const companies = (await CompanyService.retrieve("top=all")).data;
                return companies.map((com) => {
                    return {
                        key: com.id,
                        title: com.name
                    };
                });
            },
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.company ? <Text size={'small'}>{item.company.name}</Text> : null}
                    {item.order ? <>
                        {item.order.company ? <Link href={`/users/companies/${item.order.company.id}`} target={'_blank'}>
                            <Text size={'small'}>{item.order.company.name}</Text>
                        </Link> : null}
                    </> : null}
                </Stack>;
            }
        },
        {
            key: "subsidiary",
            name: "Subsidiary",
            fieldName: 'subsidiary_id',
            minWidth: 150,
            maxWidth: 150,
            enableFilter: true,
            onRetrieveFilterOptions: async () => {
                const subsidiaries = await SubsidiariesService.retrieve();
                return subsidiaries.map((sub) => {
                    return {
                        key: sub.id,
                        title: sub.name,
                        secondaryText: sub.npwp
                    };
                });
            },
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.subsidiary ? <Link href={`/settings/subsidiaries/${item.subsidiary.id}`} target={'_blank'}>
                        <Text size={'small'}>{item.subsidiary.name}</Text>
                    </Link> : null}
                    {item.order ? <Link href={`/settings/subsidiaries/${item.order.subsidiary.id}`} target={'_blank'}>
                        <Text size={'small'}>{item.order.subsidiary.name}</Text>
                    </Link> : null}
                </Stack>;
            }
        },
        {
            key: "orderDetails",
            name: "Order Details",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 8 }}>
                    {item.order ? <>
                        <SelectOrder selected={item.order}
                            disabled={true}
                            hideAddress
                            hideLabel
                            onChange={() => { }} />
                    </> : null}
                </Stack>;
            }
        },
        {
            key: "relatedItems",
            name: "Related Items",
            minWidth: 350,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                const invoiceItems = item.items || [];
                const orderItems = item.orderItems || [];

                if (invoiceItems.length === 0 && orderItems.length === 0) {
                    return <Stack styles={{ root: { padding: '4px 0px' } }}>
                        <Text size={'small'} style={{ color: '#8a8886', fontStyle: 'italic' }}>-</Text>
                    </Stack>;
                }

                return <Stack horizontal wrap tokens={{ childrenGap: 6 }} styles={{ root: { padding: '4px 0px' } }}>
                    {invoiceItems.map((invoiceItem, idx) => {
                        return <div key={`inv-item-${idx}`} style={{ whiteSpace: 'nowrap' }}>
                            <Tag size={'small'} text={`${invoiceItem.name} (Rp. ${GeneralService.getNumberWithSeparator(invoiceItem.invoiceAmount)})`} />
                        </div>;
                    })}
                    {orderItems.map((orderItem, idx) => {
                        const details = orderItem.addon || orderItem.container || orderItem.goods || orderItem.truck;

                        let text = "";
                        if (orderItem.addon) {
                            text += orderItem.addon.name || 'Addon';
                        } else if (orderItem.container) {
                            const contNum = orderItem.container.containerNumber;
                            text += `Container ${contNum && contNum.trim() !== '' && contNum !== 'null' ? contNum : `#${idx + 1}`}`;
                        } else if (orderItem.truck) {
                            const truckNum = orderItem.truck.vehicleRegistrationNumber || orderItem.truck.truck?.registrationNumber;
                            text += `Truck ${truckNum && truckNum.trim() !== '' && truckNum !== 'null' ? truckNum : `#${idx + 1}`}`;
                        } else if (orderItem.goods) {
                            text += orderItem.goods.goods?.name || 'Goods';
                        } else {
                            text += `Item #${idx + 1}`;
                        }

                        const priceVal = details?.invoicePrice !== undefined ? details.invoicePrice : (orderItem as any).amount || "0";
                        text += ` (Rp. ${GeneralService.getNumberWithSeparator(priceVal || "0")})`;

                        return <div key={`ord-item-${idx}`} style={{ whiteSpace: 'nowrap' }}>
                            <Tag size={'small'} text={text} />
                        </div>;
                    })}
                </Stack>;
            }
        },
        {
            key: "notes",
            name: "Notes",
            minWidth: 150,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item.notes || "-"}</Text>
                </Stack>;
            }
        },
        {
            key: "invoiceDate",
            name: "Created Date",
            minWidth: 100,
            maxWidth: 100,
            enableOrderBy: true,
            fieldName: 'created_at',
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{moment(item.createdAt).format("DD/MM/YYYY")}</Text>
                </Stack>;
            }
        },
        {
            key: "dueDate",
            name: "Actioned on",
            minWidth: 100,
            maxWidth: 100,
            enableOrderBy: true,
            fieldName: 'created_at',
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                const date = item.createdAt || item.paidOn || item.dueDate;
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{date ? moment(date).format("DD/MM/YYYY") : "-"}</Text>
                </Stack>;
            }
        },
        {
            key: "amount",
            name: "Amount",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                let invoiceAmount = item.invoiceAmount;

                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 5 }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(Number(invoiceAmount))}</Text>
                    <Stack className={'divider'}></Stack>
                    <Stack>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Stack styles={{ root: { width: 50 } }}>
                                <Text size={'small'} className={'color-red'}>Unpaid</Text>
                            </Stack>
                            <Text size={'small'} className={'color-red'}>: Rp. {GeneralService.getNumberWithSeparator(item.unpaid)}</Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Stack styles={{ root: { width: 50 } }}>
                                <Text size={'small'} className={'color-green'}>Paid</Text>
                            </Stack>
                            <Text size={'small'} className={'color-green'}>: Rp. {GeneralService.getNumberWithSeparator(item.paid)}</Text>
                        </Stack>
                    </Stack>
                </Stack>;
            }
        },
        {
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                let statusVariant: TTagVariant = 'success';
                if (item.status === 'Cancelled') {
                    statusVariant = 'error';
                } else if (item.status === 'Unpaid') {
                    statusVariant = 'warning';
                }

                return <Stack horizontal>
                    <Tag size={'small'} variant={statusVariant} text={item.status} />
                </Stack>;
            }
        }
    ];

    useEffect(() => {
        if (!props.invoices) {
            _onRetrieveInvoices();
        } else {
            setLoaded(true);
        }
    }, [keyword, orderBy, filterBy, selectedFilter]);

    useEffect(() => {
        const orderByQs = (props.qs || []).find((qs) => qs.toLowerCase().startsWith('order_by'));
        const orderByDirectionQs = (props.qs || []).find((qs) => qs.toLowerCase().startsWith('order_by_direction'));

        if (orderByQs) {
            setOrderBy({
                fieldName: orderByQs.split("=")[1],
                direction: orderByDirectionQs ? orderByDirectionQs.split("=")[1] as any : "asc"
            });
        }
    }, [props.qs]);

    const _onRetrieveInvoices = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = props.qs ? JSON.parse(JSON.stringify(props.qs)) : [];
            qs.push(`top=${GlobalConfig.defaultTop}`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`); }
            if (orderBy) {
                const orderByIndex = qs.findIndex((qs) => qs.toLowerCase().startsWith('order_by='));
                const orderByDirectionIndex = qs.findIndex((qs) => qs.toLowerCase().startsWith('order_by_direction='));

                if (orderByIndex > -1) {
                    qs.splice(orderByIndex, 1, 'order_by=' + orderBy.fieldName);
                } else {
                    qs.push("order_by=" + orderBy.fieldName);
                }

                if (orderByDirectionIndex > -1) {
                    qs.splice(orderByDirectionIndex, 1, 'order_by_direction=' + orderBy.direction);
                } else {
                    qs.push("order_by_direction=" + orderBy.direction);
                }
            }

            const groupedFilter: { key: string, options: FilterOptionProps[] }[] = [];
            selectedFilter.forEach((sel) => {
                const gfidx = groupedFilter.findIndex((gf) => gf.key === sel.filterby);
                if (gfidx > -1) {
                    groupedFilter[gfidx].options.push(sel);
                } else {
                    groupedFilter.push({
                        key: sel.filterby,
                        options: [sel]
                    });
                }
            });

            groupedFilter.forEach((gf) => {
                if (gf.key === 'customer') {
                    qs.push(`search=${gf.options.map((opt) => opt.title).join(';')}`);
                } else if (gf.key === 'subsidiary') {
                    qs.push(`subsidiary_id=${gf.options.map((opt) => opt.key).join(';')}`);
                } else if (gf.key === 'status') {
                    qs.push(`status=${gf.options.map((opt) => opt.key).join(';')}`);
                } else {
                    qs.push(`${gf.key}=${gf.options.map((opt) => opt.key).join(';')}`);
                }
            });

            (filterBy || []).forEach((f) => {
                qs.push(`${f.key}=${f.selected.map((sel) => sel.key).join(";")}`);
            });

            const results = await IncomeInvoiceService.retrieve(qs.join("&"));
            setInvoices(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_outcomes_list_error',
                text: 'Failed to retrieve outcomes list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    };

    const _onKeywordChanged = (value?: string) => {
        setLoaded(false);
        setKeyword(value || "");
    };

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveInvoice(undefined);

        if (refresh) { _onRetrieveInvoices(); }
    };

    const getCommandBar = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search customer name, order #, invoice # ..."}
                        onSearch={_onKeywordChanged}
                        onChange={(ev, newValue) => {
                            if (!newValue) { _onKeywordChanged(""); }
                        }}
                        styles={{ root: { minWidth: 320, marginRight: 10 } }} />;
                }
            });
        }

        if (!props.hideFilter) {
            items.push({
                key: "filter",
                onRender: () => {
                    return <Stack styles={{ root: { marginRight: 10 } }}>
                        <IncomeInvoicesListFilterButton selected={selectedFilter}
                            filters={props.filters}
                            onChange={(selected) => {
                                setSelectedFilter([...selected]);
                            }} />
                    </Stack>;
                }
            });
        }

        farItems.push({
            key: "exportExcel",
            text: "Export Excel",
            iconProps: { iconName: "ExcelDocument" },
            onRender: () => {
                return <DefaultButton
                    text={exporting ? "Exporting..." : "Export Excel"}
                    iconProps={{ iconName: "ExcelDocument" }}
                    disabled={exporting || !invoices || invoices.length === 0}
                    onClick={handleExportExcel}
                    styles={{
                        root: {
                            marginLeft: 10,
                            borderColor: '#107c41',
                            color: '#107c41',
                            backgroundColor: '#ffffff'
                        },
                        rootHovered: {
                            backgroundColor: '#f0fdf4',
                            borderColor: '#15803d',
                            color: '#15803d'
                        }
                    }}
                />;
            }
        });

        const hasCreatePermission = PermissionsService.hasPermission(['incomes.invoices.create'], user.permissions);
        if (!props.hideCreateButton && hasCreatePermission) {
            farItems.push({
                key: "register",
                text: "Create Invoice",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create Invoice"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create'); }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    };

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <ListItems key={"__sys_listitems_incomeInvoices"}
            pagination={pagination}
            shownColumns={props.columns || shownColumns}
            loaded={loaded}
            items={invoices}
            columns={columns}
            orderBy={orderBy}
            itemText={'invoices'}
            quickFilters={props.quickFilters}
            filterBy={filterBy}
            onOrderByChange={setOrderBy}
            onPageChange={_onRetrieveInvoices}
            onSearch={_onKeywordChanged}
            onFilterByChange={(filterBy) => setFilterBy([...filterBy])}
            commandBar={getCommandBar()} />
        {activeSurface === 'details' && activeInvoice ? <IncomeInvoiceDetails invoiceId={activeInvoice.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'create' ? <IncomeInvoiceForm orderId={props.orderId} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack >;
});

export default IncomeInvoicesList;
